/**
 * Inbox domain — incoming gifts and contact requests. Every social item is
 * consent-gated: accept / decline / block. Uses the locked Gift/ContactRequest
 * status tokens (see @jod/schemas + CLAUDE.md canonical state tokens).
 */

export type InboxKind = "gift" | "contact";
export type InboxState =
  | "pending"
  | "accepted"
  | "fulfilled"
  | "declined"
  | "expired"
  | "staff_rejected";

export interface InboxItem {
  id: string;
  kind: InboxKind;
  /** Sender table code, e.g. "C2". */
  fromTable: string;
  /** Drink name for gifts; channel ("IG"/"chat") for contact requests. */
  subject: string;
  subjectTh?: string;
  iconName?: string;
  message?: string;
  state: InboxState;
}

export type InboxAction = "accept" | "decline" | "block" | "fulfill" | "expire" | "staff_reject";

/**
 * Apply a consent action. Accept/decline transition the item in place; block
 * removes it entirely (and, upstream, blocks the sender + files an AuditLog).
 * Fulfilment is intentionally two-step for gifts: guest accepts first, then
 * staff can fulfil or reject/refund the accepted ticket.
 */
export function applyInboxAction(
  items: readonly InboxItem[],
  id: string,
  action: InboxAction,
): InboxItem[] {
  if (action === "block") {
    return items.filter((i) => i.id !== id);
  }

  return items.map((item) => {
    if (item.id !== id) return item;

    switch (action) {
      case "accept":
        return item.state === "pending" ? { ...item, state: "accepted" } : item;
      case "decline":
        return item.state === "pending" ? { ...item, state: "declined" } : item;
      case "fulfill":
        return item.kind === "gift" && item.state === "accepted"
          ? { ...item, state: "fulfilled" }
          : item;
      case "expire":
        return item.state === "pending" ? { ...item, state: "expired" } : item;
      case "staff_reject":
        return item.kind === "gift" && (item.state === "pending" || item.state === "accepted")
          ? { ...item, state: "staff_rejected" }
          : item;
    }
    return item;
  });
}

/** Count of items still awaiting a response (drives the nav badge). */
export function pendingCount(items: readonly InboxItem[]): number {
  return items.filter((i) => i.state === "pending").length;
}

export function isTerminalInboxState(state: InboxState): boolean {
  return ["fulfilled", "declined", "expired", "staff_rejected"].includes(state);
}
